import { Avatar, Box, Chip, Stack, Typography } from "@mui/material";
import type { JSX } from "react";
import { globalFormatCurrencyBRL } from "../../../shared/utils";

/* ---------------------------- STATUS CHIP ---------------------------- */
export function StatusChip({ status }: { status?: string }) {
    const palette: Record<string, { label: string; color: string }> = {
        pending: { label: "Pendente", color: "#ffb74d" },
        open: { label: "Aguardando contratação", color: "#64b5f6" },
        canceled: { label: "Cancelado", color: "#ef9a9a" },
        expirado: { label: "Expirado", color: "#bdbdbd" },
        approved: { label: "Aprovado", color: "#81c784" },
    };

    const s = palette[status ?? ""] ?? {
        label: status ?? "Indefinido",
        color: "#bdbdbd"
    };

    return (
        <Chip
            label={
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: s.color,
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: "1rem",
                            fontWeight: 600,
                            letterSpacing: 0.3
                        }}
                    >
                        {s.label}
                    </Typography>
                </Stack>
            }
            sx={{
                border: `1px solid ${s.color}`,
                color: s.color,
                backgroundColor: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(4px)",
                px: 1.4,
                py: 0.2,
                height: 26,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
            }}
        />
    );
}

/* ---------------------------- INFO ROW ---------------------------- */
export function InfoRow({
    icon,
    label,
    value,
}: {
    icon: JSX.Element;
    label: string;
    value?: string | JSX.Element | null;
}) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Avatar
                variant="rounded"
                sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    width: 34,
                    height: 34,
                    fontSize: 18,
                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
                }}
            >
                {icon}
            </Avatar>

            <Stack spacing={0.3} flex={1}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                    {label}
                </Typography>

                <Typography variant="body2" fontWeight={500}>
                    {value || "—"}
                </Typography>
            </Stack>
        </Stack>
    );
}

/* ---------------------- DATA PREPARATION LOGIC ---------------------- */
/**
 * Centraliza toda a lógica de derivação e formatação
 */
export function prepareFireQuoteData(data: any) {
    // formatadores
    const fmt = (n?: number | string) =>
        globalFormatCurrencyBRL(String(Number(n ?? 0)));

    const fdate = (d?: string) =>
        d ? new Date(d).toLocaleDateString() : "—";

    // nomes
    const insurerName = data?.insuranceCompany?.name ?? "Seguradora não informada";
    const realEstateName = data?.client?.name ?? "Imobiliária não informada";

    // valores principais
    const totalPrize = Number(data?.insurance_prize ?? 0);
    const installments = Number(data?.installment_number ?? 1);
    const installmentValue = installments > 0 ? totalPrize / installments : totalPrize;

    // vigência
    let endDate: Date | null = null;
    if (data?.validity_start) {
        const d = new Date(data.validity_start);
        d.setMonth(d.getMonth() + Number(data?.monthly_validity ?? 12));
        endDate = d;
    }

    // cobertura
    const coverageLabel = `Prédio ${data?.pc_predial ?? "—"}% - Conteúdo ${data?.pc_content ?? "—"}%`;

    // endereço completo
    const addressLine = [
        [data?.address_street, data?.address_number, data?.address_complement]
            .filter(Boolean)
            .join(", "),
        [
            data?.address_district,
            `${data?.address_city} - ${data?.address_state}`,
            data?.address_zipcode ? `CEP: ${data.address_zipcode}` : null,
        ]
            .filter(Boolean)
            .join(" | "),
    ]
        .filter(Boolean)
        .join(" • ");

    return {
        fmt,
        fdate,
        insurerName,
        realEstateName,
        totalPrize,
        installments,
        installmentValue,
        endDate,
        coverageLabel,
        addressLine,
    };
}
